"use client";
import { useRef, useEffect, useState } from "react";
import { Capability } from "@/components/playground/prompt-types";
import { Card, CardContent } from "@/components/ui/card";
import CapabilityPill from "./capability-pill";
import CodeExample from "./code-example";

type BoundingBoxData = {
  x_min: number;
  y_min: number;
  x_max: number;
  y_max: number;
}[];

export default function InferenceDetect({
  image,
  result,
  query,
  duration,
  imageFile,
}: {
  image: string;
  result: BoundingBoxData;
  query: string;
  duration?: number;
  imageFile?: File;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [scaledDimensions, setScaledDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });

  // Draw detection results on canvas when component mounts or results change
  useEffect(() => {
    // We've improved type handling in the component logic

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Track if the effect is still current
    let isMounted = true;

    // Create an image to draw
    const imgElement = new window.Image();

    // Set up onload before setting src to avoid race conditions
    imgElement.onload = () => {
      // Check if component is still mounted
      if (!isMounted || !ctx || !canvas) return;
      // Calculate scaled dimensions (max 512px for largest dimension)
      const MAX_DIMENSION = 512;
      let scaledWidth = imgElement.width;
      let scaledHeight = imgElement.height;

      // Scale down if either dimension exceeds the max
      if (scaledWidth > MAX_DIMENSION || scaledHeight > MAX_DIMENSION) {
        if (scaledWidth >= scaledHeight) {
          // Width is the larger dimension
          const aspectRatio = scaledHeight / scaledWidth;
          scaledWidth = MAX_DIMENSION;
          scaledHeight = MAX_DIMENSION * aspectRatio;
        } else {
          // Height is the larger dimension
          const aspectRatio = scaledWidth / scaledHeight;
          scaledHeight = MAX_DIMENSION;
          scaledWidth = MAX_DIMENSION * aspectRatio;
        }
      }

      // Set canvas dimensions to 2x scaled size for higher DPI rendering
      canvas.width = scaledWidth * 2;
      canvas.height = scaledHeight * 2;

      // Let Tailwind handle responsive sizing, just set aspect ratio
      canvas.style.aspectRatio = `${scaledWidth} / ${scaledHeight}`;

      // Clear the canvas first to avoid artifacts
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Scale up all drawing operations by 2x to improve dpi
      ctx.scale(2, 2);

      // Only update dimensions if they've changed to prevent render loops
      if (
        scaledDimensions.width !== scaledWidth ||
        scaledDimensions.height !== scaledHeight
      ) {
        setScaledDimensions({ width: scaledWidth, height: scaledHeight });
      }

      // Draw the image at the original scaled dimensions
      ctx.drawImage(imgElement, 0, 0, scaledWidth, scaledHeight);

      const boxes = Array.isArray(result) ? (result as BoundingBoxData) : [];

      // Only add dimming if we have boxes to show
      if (boxes.length > 0) {
        // Draw dimming overlay first (30% dim)
        ctx.fillStyle = "rgba(0, 0, 0, 0.3)";
        ctx.fillRect(0, 0, scaledWidth, scaledHeight);

        ctx.strokeStyle = "rgba(40, 141, 255, 0.9)";
        ctx.lineWidth = 3;

        // First pass: clear dimming for all boxes
        boxes.forEach((box) => {
          const x = box.x_min * scaledWidth;
          const y = box.y_min * scaledHeight;
          const width = (box.x_max - box.x_min) * scaledWidth;
          const height = (box.y_max - box.y_min) * scaledHeight;

          // Clear the dimming inside the box by drawing the original image section
          ctx.save();
          ctx.beginPath();
          ctx.rect(x, y, width, height);
          ctx.clip();
          ctx.drawImage(imgElement, 0, 0, scaledWidth, scaledHeight);
          ctx.restore();
        });

        // Second pass: draw all borders on top
        boxes.forEach((box) => {
          const x = box.x_min * scaledWidth;
          const y = box.y_min * scaledHeight;
          const width = (box.x_max - box.x_min) * scaledWidth;
          const height = (box.y_max - box.y_min) * scaledHeight;

          // Draw the blue border
          ctx.strokeRect(x, y, width, height);
        });
      }
    };

    // Set the image source after setting up onload
    imgElement.src = image;

    // Clean up function
    return () => {
      isMounted = false;
    };
  }, [image, result, scaledDimensions.width, scaledDimensions.height]);

  return (
    <Card>
      <CardContent className="mt-6">
        <div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            <div className="flex-shrink-0 w-full sm:w-auto">
              <canvas
                ref={canvasRef}
                className="rounded-lg w-full max-w-full sm:w-auto sm:max-w-md h-auto"
              />
            </div>

            <div className="flex flex-col gap-2 min-w-0 flex-grow">
              <div>
                <CapabilityPill capability={Capability.ObjectDetection} />
              </div>
              <div>
                <div className="text-xs font-black text-muted-foreground">
                  PROMPT
                </div>
                <div className="text-sm whitespace-pre-wrap break-words">{query.trimStart()}</div>
              </div>

              {/* Only show a message if no results are detected */}
              {Array.isArray(result) && result.length === 0 && (
                <div className="mt-2 text-sm text-amber-500">
                  &ldquo;No objects detected in the image.&rdquo;
                </div>
              )}
              {duration && (
                <div className="text-xs text-muted-foreground mt-2">
                  Response time: {duration.toFixed(0)}ms
                </div>
              )}
            </div>
          </div>
        </div>
        {imageFile && (
          <CodeExample
            capability={Capability.ObjectDetection}
            prompt={query}
            imageFile={imageFile}
          />
        )}
      </CardContent>
    </Card>
  );
}
